import React, { useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { base_url } from "Backend/api";
import { getFcmToken } from "getFcmToken";
import { registerFcmToken } from "Backend/registerFcmToken";
import { validatePhoneNumber } from "utils/validatePhoneNumber";

const GoogleRegisterButton = () => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(320);
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (!buttonRef.current) return;

    const updateWidth = () => {
      const width = Math.round(buttonRef.current.getBoundingClientRect().width);
      if (width > 0) setButtonWidth(width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(buttonRef.current);

    return () => observer.disconnect();
  }, []);

  const finishLogin = (targetPath = "/restaurant-home-page") => {
    setTimeout(() => {
      navigate(targetPath);
    }, 300);
  };

  const handleClosePhoneDialog = () => {
    setShowPhoneDialog(false);
    setPhone("");
    setPhoneError("");
    finishLogin();
  };

  const updatePhone = async () => {
    const normalizedPhone = phone.trim();
    const phoneValidationError = validatePhoneNumber(normalizedPhone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      toast.error(phoneValidationError);
      return;
    }

    try {
      setPhoneLoading(true);

      const response = await fetch(`${base_url}/api/me/phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${pendingToken}`,
        },
        body: JSON.stringify({
          phone: normalizedPhone,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status === false) {
        throw new Error(data.message || "فشل حفظ رقم الهاتف");
      }

      localStorage.setItem("phone", normalizedPhone);
      if (pendingUser) {
        const updatedUser = { ...pendingUser, phone: normalizedPhone };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setPendingUser(updatedUser);
      }

      toast.success("تمت إضافة رقم الهاتف بنجاح");
      setShowPhoneDialog(false);
      setPhone("");
      setPhoneError("");
      finishLogin();
    } catch (error) {
      console.error(error);
      toast.error("فشل حفظ رقم الهاتف");
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${base_url}/api/login/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          credential: credentialResponse.credential
        }),
      });

      const data = await res.json();

      if (res.ok && data.status) {
        const user = data?.data?.user || {};
        const token = data?.data?.token || "";

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("token", token);
        localStorage.setItem("slug", user.slug);

        setPendingToken(token);
        setPendingUser(user);

        if (user?.phone) {
          localStorage.setItem("phone", user.phone);
        } else {
          localStorage.removeItem("phone");
        }

        const fcmToken = await getFcmToken();
        if (fcmToken) {
          await registerFcmToken(fcmToken, token);
        }

        if (user?.phone) {
          toast.success(data.message || "تم تسجيل الدخول بنجاح");
          finishLogin();
        } else {
          setShowPhoneDialog(true);
        }
      } else {
        toast.error(data.message || "حدث خطأ أثناء تسجيل الدخول");
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل الاتصال بالخادم");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="w-full mb-4">
        <div
          ref={buttonRef}
          className="relative w-full h-[46px] rounded-full border border-[#E8E8E8] bg-[#1A2438] shadow-[0_5px_14px_rgba(0,0,0,0.16)]"
        >
          <button
            type="button"
            className="w-full h-full rounded-full flex items-center justify-center gap-3 text-white font-[cairo] font-semibold pointer-events-none"
          >
            {isLoading ? (
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-white/90 animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-white/80 animate-pulse [animation-delay:120ms]" />
                <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse [animation-delay:240ms]" />
              </span>
            ) : (
              <>
                <img src="/assets/images/google-icon.svg" alt="Google" className="w-6 h-6 object-contain" />
                <span className="text-[14px] font-medium leading-none">سجل باستخدام غوغل</span>
              </>
            )}
          </button>

          <div className="absolute inset-0 opacity-0">
            <GoogleLogin
              shape="pill"
              theme="filled_black"
              size="large"
              text="signin_with"
              locale="ar"
              width={buttonWidth}
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setIsLoading(false);
                toast.error("فشل تسجيل الدخول باستخدام Google");
              }}
            />
          </div>
        </div>
      </div>

      {showPhoneDialog && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/35 px-5">
          <div
            dir="rtl"
            className="relative w-full max-w-[660px] rounded-[8px] bg-white p-5 shadow-2xl md:p-7"
          >
            

            <div className="mx-auto flex justify-center">
              <div className="flex h-[120px] w-[120px] items-center justify-center ">
                <img
                  src="/assets/images/upphone.svg"
                  alt="phone"
                  className="h-[100px] w-[100px] object-contain"
                />
              </div>
            </div>

            <h2 className="text-center font-[cairo] text-[18px] font-bold text-[#1F2937] md:text-[20px]">
              أضف رقم هاتف
            </h2>

            <p className="mx-auto mt-1 max-w-[490px] text-center font-[cairo] text-[13px] leading-2 text-[#777777] md:text-[15px]">
              رقم الهاتف مطلوب لتمكين مندوب التوصيل من التواصل معك عند الحاجة
              لتوضيح العنوان.
            </p>

            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError("");
              }}
              onBlur={() => setPhoneError(validatePhoneNumber(phone) || "")}
              placeholder="+963 999 9999 99"
              className={`mt-2 w-full font-[cairo] rounded-[4px] border px-4 py-3 text-left text-[14px] text-[#1F2937] outline-none placeholder:text-[14px] placeholder:text-[#D6D6D6] focus:border-[#F93209] focus:outline-none focus:ring-0 ${
                phoneError ? "border-red-500" : "border-[#E5E7EB]"
              }`}
            />
            {phoneError && (
              <p className="mt-1 text-right font-[cairo] text-xs text-red-500">
                {phoneError}
              </p>
            )}

            <button
              type="button"
              onClick={updatePhone}
              disabled={phoneLoading}
              className="mt-3 w-full rounded-[4px] bg-[#FF3B0A] py-3 font-[cairo] text-[16px] font-semibold text-white disabled:opacity-70"
            >
              {phoneLoading ? "جارِ الحفظ..." : "أضف الرقم"}
            </button>
          </div>
        </div>
       )} 
    </>
  );
};

export default GoogleRegisterButton;